// @spec SCF-004
// @spec SCF-008
// @spec IAM-STR-001
// @spec IAM-STR-008
// @spec IAM-STR-013
// @spec IAM-STR-034
// @spec SHL-STR-001
// @spec SHL-STR-002
// @spec SHL-STR-005
// @spec SHL-STR-007
// @spec SHL-STR-052
import { useOrganizationList, useUser } from '@clerk/clerk-react';
import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';

import { SystemAnnouncementBanner } from '@/components/announcements';
import { Breadcrumbs } from '@/components/breadcrumb';
import { ApplicationHeader } from '@/components/header';
import { RoutePending } from '@/components/route-pending';
import { AppShell } from '@/components/shell';
import { SidebarNavigation } from '@/components/sidebar';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { MfaEnforcementBanner } from '@/features/security';
import { useOrganizationSettings } from '@/features/settings/organization/api/use-organization-settings';
import { useLocaleSync } from '@/hooks/use-locale-sync';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: ({ context, location }) => {
    // Wait for Clerk to finish loading before checking auth
    if (!context.auth.isLoaded) {
      // Return undefined to let the pending component show
      return;
    }

    if (!context.auth.isAuthenticated) {
      // TanStack Router requires throwing the redirect result
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/sign-in',
        search: {
          redirect_url: location.href,
        },
      });
    }
  },
  pendingComponent: RoutePending,
  component: AuthenticatedLayout,
});

/**
 * Hook that checks if the current user should be blocked from accessing
 * the app due to MFA requirement with expired grace period.
 *
 * Returns true if the user should be redirected to security settings.
 */
function useMfaEnforcementRedirect() {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { data: orgSettings } = useOrganizationSettings();
  const location = useLocation();
  const navigate = useNavigate();

  const shouldBlock = useMemo(() => {
    if (!isUserLoaded || !orgSettings) return false;

    const { securitySettings } = orgSettings;

    // No enforcement if MFA not required or user already has MFA
    if (!securitySettings.requireMfa || user?.twoFactorEnabled) return false;

    // Check if grace period has expired
    const { mfaRequiredAt, mfaGracePeriodDays } = securitySettings;

    if (!mfaRequiredAt || mfaGracePeriodDays <= 0) {
      // Immediate enforcement (no grace period)
      return true;
    }

    const requiredDate = new Date(mfaRequiredAt);
    const graceDeadline = new Date(
      requiredDate.getTime() + mfaGracePeriodDays * 24 * 60 * 60 * 1000
    );
    return new Date() >= graceDeadline;
  }, [isUserLoaded, orgSettings, user?.twoFactorEnabled]);

  useEffect(() => {
    if (!shouldBlock) return;

    // Don't redirect if already on security settings page (prevent infinite loop)
    if (location.pathname.startsWith('/settings/security')) return;

    void navigate({ to: '/settings/security' });
  }, [shouldBlock, location.pathname, navigate]);
}

function AuthenticatedLayout() {
  // Check if user has other organizations to switch to
  const { userMemberships, isLoaded } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const hasOtherOrganizations = (userMemberships.data?.length ?? 0) > 0;

  // Sync i18next language with user's backend locale preference
  useLocaleSync();

  // Enforce MFA redirect for users past the grace period
  useMfaEnforcementRedirect();

  // Gate on org list readiness to prevent child components from rendering
  // before organization context is available (fixes race conditions in
  // NIS2, Employee Hub, and other org-dependent pages).
  if (!isLoaded) {
    return <RoutePending />;
  }

  return (
    <>
      {/* Auditor mode banner - shows when in auditor mode */}
      <AuditorModeBanner hasOtherOrganizations={hasOtherOrganizations} />

      {/* System announcements banner - shows active platform announcements */}
      <SystemAnnouncementBanner />

      {/* MFA enforcement banner - shows when org requires MFA and user hasn't set it up */}
      <MfaEnforcementBanner />

      <AppShell
        headerContent={<ApplicationHeader />}
        sidebarContent={<SidebarNavigation />}
        breadcrumbs={<Breadcrumbs />}
      >
        <Outlet />
      </AppShell>
    </>
  );
}
